// QBR — shared concurrency gate + transient retry for FMX reads.
//
// Why this exists:
// A QBR pull fans out across every work-request / work-task module scope, the
// prior-quarter summary, the 4-quarter trend, adoption (/v1/users), and the
// building / equipment / invoice lookups. `fetchAllModulesQbrData` fires those
// as unbounded concurrent single-shot `/api/fmx` calls, so a tenant with a few
// modules puts a burst of dozens of simultaneous authenticated requests on the
// customer's FMX site.
//
// Under that burst a subset of requests intermittently came back 401. FMX's
// ApiCore Basic-auth path returns 401 only for a malformed header, an
// unresolvable tenant/organization, or a bad email/password — NOT for
// throttling (that's 429) and there is no account lockout. The creds are valid
// (the connect check and the QBR setup step's /v1/user-types call succeed with
// them in the same session), so the failures line up with transient tenant/org
// resolution under concurrent load rather than a static credential problem.
//
// The migration sync path already avoids this by capping in-flight requests
// through the batch proxy (api/fmx.js BATCH_CONCURRENCY = 4). This module gives
// the QBR single-shot pulls the same global ceiling. The limiter is
// module-scoped, so it bounds the COMBINED in-flight count across every QBR
// fetcher (fmxData.js + fmxUsers.js) rather than per file.

import { fmxFetch, fmxErrorMessage } from '../apiClient.js';

// Match the batch proxy's server-side ceiling so QBR reads apply the same
// pressure on the customer site as the migration flows do.
const MAX_INFLIGHT = 4;

// Transient upstream statuses worth a bounded retry. 401 is deliberately NOT
// here: FMX returns 401 only for a malformed header, an unresolvable
// tenant/org, or bad credentials — all deterministic for a given project, so a
// retry just doubles the failed auth attempts (and the console noise) without
// ever succeeding. Only genuinely transient upstream conditions are retried.
const RETRY_BACKOFF_MS = [300, 800];
const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);

let inFlight = 0;
const waiters = [];

function acquire() {
  if (inFlight < MAX_INFLIGHT) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise(resolve => waiters.push(resolve));
}

function release() {
  // Hand the freed slot straight to the next waiter (in-flight count is
  // unchanged in that case); only drop the count when nobody is waiting.
  const next = waiters.shift();
  if (next) next();
  else inFlight -= 1;
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Drop-in replacement for fmxFetch for the QBR read paths. Returns the same
// raw Response object (callers still use res.ok / res.status /
// res.headers.get / res.json). The response body is never read here, so
// callers can consume it normally.
export async function qbrFmxFetch(opts) {
  await acquire();
  try {
    return await fetchWithRetry(opts);
  } finally {
    release();
  }
}

// Build a diagnostic error string that includes FMX's own error body, so a
// failed pull says WHY it failed rather than just the status. This matters for
// 401s: FMX distinguishes "Organization not found" (wrong site URL / the
// hostname doesn't resolve to a tenant) from an invalid-credentials message,
// and the two point at different fixes. Reading the body is safe here — the
// caller is already treating the response as an error.
export async function fmxErrorText(res, endpoint) {
  let detail = '';
  try {
    const body = await res.json();
    detail = fmxErrorMessage(body, res.status);
  } catch { /* non-JSON or empty body — fall back to the bare status */ }
  const suffix = detail && detail !== `HTTP ${res.status}` ? ` — ${detail}` : '';
  return `FMX returned ${res.status} for ${endpoint}${suffix}`;
}

async function fetchWithRetry(opts) {
  // Only idempotent reads are retried. QBR only issues GETs; anything else
  // passes through untouched so this can never re-send a write.
  const method = (opts?.method || 'GET').toUpperCase();
  const retryable = method === 'GET';

  let res;
  for (let attempt = 0; ; attempt += 1) {
    res = await fmxFetch(opts);
    if (res.ok || !retryable) return res;

    const maxRetries = TRANSIENT_STATUSES.has(res.status) ? RETRY_BACKOFF_MS.length : 0;
    if (attempt >= maxRetries) return res;

    await sleep(RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)]);
  }
}
